import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import CartIcon from './CartIcon'

const STORAGE_CARRITO = 'limpack_carrito_v1'

/**
 * Lee el carrito del localStorage y, si hay items y NO estás en la tienda,
 * muestra un FAB chiquito que te lleva a la tienda para terminar la compra.
 *
 * También escucha cambios al storage (mismo tab + multi-tab) para refrescar
 * el contador sin recargar.
 */
function leerCarrito() {
  try {
    const arr = JSON.parse(localStorage.getItem(STORAGE_CARRITO))
    return Array.isArray(arr) ? arr : []
  } catch { return [] }
}

export default function CarritoFlotante() {
  const location = useLocation()
  const navigate = useNavigate()
  const [carrito, setCarrito] = useState(leerCarrito)

  // Refrescar cuando cambia el storage (otra pestaña) o cuando navegamos
  // (porque el mismo tab no dispara 'storage')
  useEffect(() => {
    function onStorage(e) {
      if (e.key === STORAGE_CARRITO) setCarrito(leerCarrito())
    }
    function onCustom() { setCarrito(leerCarrito()) }
    window.addEventListener('storage', onStorage)
    window.addEventListener('lp:carrito-changed', onCustom)
    // Refrescar al montar y al cambiar de ruta
    setCarrito(leerCarrito())
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('lp:carrito-changed', onCustom)
    }
  }, [location.pathname])

  const cantidad = carrito.reduce((s, it) => s + (it.cantidad || 0), 0)
  const subtotal = carrito.reduce((s, it) => s + (it.precio_unit || 0) * (it.cantidad || 0), 0)

  // Si estás en la tienda, ya hay un FAB nativo — no duplicar
  const enTienda = location.pathname.startsWith('/cliente/tienda')
  if (enTienda || cantidad === 0) return null

  function vaciar() {
    if (!confirm('¿Vaciar el carrito? Se van a perder los productos que tenías.')) return
    localStorage.removeItem(STORAGE_CARRITO)
    window.dispatchEvent(new Event('lp:carrito-changed'))
    setCarrito([])
  }

  return (
    <div className="lp-cf-wrap">
      <button
        type="button"
        className="lp-cf-main"
        onClick={() => navigate('/catalogo')}
        aria-label="Volver a la tienda para terminar tu compra"
      >
        <span className="lp-cf-icon"><CartIcon size={22} /></span>
        <span className="lp-cf-text">
          <strong>Tenés {cantidad} {cantidad === 1 ? 'producto' : 'productos'} en el carrito</strong>
          <em>${subtotal.toLocaleString('es-UY')} · seguí comprando o terminá tu pedido</em>
        </span>
        <span className="lp-cf-arrow">›</span>
      </button>
      <button
        type="button"
        className="lp-cf-close"
        onClick={vaciar}
        aria-label="Vaciar carrito"
        title="Vaciar carrito"
      >×</button>

      <style>{`
        .lp-cf-wrap {
          position: fixed;
          left: 50%;
          transform: translateX(-50%);
          bottom: calc(env(safe-area-inset-bottom, 0) + 84px);
          z-index: 45;
          display: flex; align-items: stretch; gap: 6px;
          max-width: calc(100% - 24px);
          width: 380px;
        }
        .lp-cf-main {
          flex: 1;
          display: flex; align-items: center; gap: 12px;
          background: linear-gradient(135deg, #1a5cb8, #00A8B5);
          color: #fff; border: 0;
          padding: 12px 14px;
          border-radius: 14px;
          font-family: inherit;
          box-shadow: 0 10px 28px rgba(0,196,212,.4), 0 2px 6px rgba(15,31,61,.18);
          cursor: pointer;
          text-align: left;
          transition: transform .12s;
        }
        .lp-cf-main:hover { transform: translateY(-2px) }
        .lp-cf-icon { font-size: 22px; flex-shrink: 0 }
        .lp-cf-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; line-height: 1.25 }
        .lp-cf-text strong { font-size: 13px; font-weight: 800 }
        .lp-cf-text em {
          font-style: normal; font-size: 11px;
          color: rgba(255,255,255,.85);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .lp-cf-arrow { font-size: 22px; opacity: .9; flex-shrink: 0; font-weight: 300 }
        .lp-cf-close {
          width: 38px;
          background: rgba(15,31,61,.85); color: #fff; border: 0;
          border-radius: 12px;
          font-size: 20px; font-weight: 400;
          cursor: pointer; font-family: inherit;
          box-shadow: 0 4px 12px rgba(15,31,61,.25);
          flex-shrink: 0;
        }
        .lp-cf-close:hover { background: #b91c1c }

        @media (min-width: 900px) {
          /* En desktop NO hay bottom-nav: lo subimos a la derecha */
          .lp-cf-wrap {
            left: auto; right: 24px; bottom: 24px;
            transform: none;
            width: 360px;
          }
        }
      `}</style>
    </div>
  )
}
